<template>
  <label :class="wrapClasses">
        <span :class="radioClasses">
            <span :class="innerClasses"></span>
            <input
              type="radio"
              :class="inputClasses"
              :disabled="disabled"
              :checked="currentValue"
              :name="groupName"
              @change="change"
              @focus="onFocus"
              @blur="onBlur">
        </span><slot :currentValue="currentValue">{{ label }}</slot>
  </label>
</template>

<script>
  import { findComponentUpward } from './../assist';
  import Emitter from './../emitter';

  const prefixCls = 'am-radio';

  export default {
    name: 'Radio',
    mixins: [ Emitter ],
    props: {
      value: {
        type: [String, Number, Boolean],
        default: false
      },
      trueValue: {
        type: [String, Number, Boolean],
        default: true
      },
      falseValue: {
        type: [String, Number, Boolean],
        default: false
      },
      label: {
        type: [String, Number]
      },
      custom: {
        type: [Boolean],
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        currentValue: this.value,
        group: false,
        groupName: this.name,
        parent: findComponentUpward(this, 'RadioGroup'),
        focusWrapper: false,
        focusInner: false
      };
    },
    computed: {
      wrapClasses () {
        return [
          `${prefixCls}-wrapper`,
          {
            [`${prefixCls}-group-item`]: this.group,
            [`${prefixCls}-wrapper-checked`]: this.currentValue,
            [`${prefixCls}-wrapper-disabled`]: this.disabled,
            [`${prefixCls}-${this.size}`]: !!this.size,
            [`${prefixCls}-focus`]: this.focusWrapper
          }
        ];
      },
      radioClasses () {
        return [
          `${prefixCls}`,
          {
            [`${prefixCls}-checked`]: this.currentValue,
            [`${prefixCls}-disabled`]: this.disabled,
            [`${prefixCls}-custom`]: this.custom
          }
        ];
      },
      innerClasses () {
        return [
          `${prefixCls}-inner`,
          {
            [`${prefixCls}-focus`]: this.focusInner
          }
        ];
      },
      inputClasses () {
        return `${prefixCls}-input`;
      }
    },
    mounted () {
      if (this.parent) {
        this.group = true;
        if (this.name && this.name !== this.parent.name) {
          /* eslint-disable no-console */
          if (console.warn) {
            console.warn('[iview] Name does not match Radio Group name.');
          }
          /* eslint-enable no-console */
        } else {
          this.groupName = this.parent.name;
        }
      }

      if (this.group) {
        this.parent.update();
      } else {
        this.update();
      }
    },
    methods: {
      change (event) {
        if (this.disabled) {
          return false;
        }

        const checked = event.target.checked;
        this.currentValue = checked;

        const value = checked ? this.trueValue : this.falseValue;
        this.$emit('input', value);

        if (this.group) {
          if (this.label !== undefined) {
            this.parent.change({
              value: this.label,
              checked: this.value
            });
          }
        } else {
          this.$emit('on-change', value);
          this.dispatch('FormItem', 'on-form-change', value);
        }
      },
      update () {
        this.currentValue = this.value === this.trueValue;
      },
      onBlur () {
        this.focusWrapper = false;
        this.focusInner = false;
      },
      onFocus () {
        if (this.group && this.parent.type === 'button') {
          this.focusWrapper = true;
        } else {
          this.focusInner = true;
        }
      }
    },
    watch: {
      value (val) {
        if (val === this.trueValue || val === this.falseValue) {
          this.update();
        } else {
          throw 'Value should be trueValue or falseValue.';
        }
      }
    }
  };
</script>

<style lang="less">
  @prefixCls: am-radio;
  .@{prefixCls}-wrapper {
    font-size: 12px;
    vertical-align: middle;
    display: inline-block;
    position: relative;
    white-space: nowrap;
    margin-right: 8px;
    cursor: pointer;
  }
  .@{prefixCls}-wrapper-checked {
    .@{prefixCls}-inner {
      border-color: #2d8cf0;
      &:after {
        opacity: 1;
        -webkit-transform: scale(1);
        transform: scale(1);
        transition: all .2s ease-in-out;
      }
    }
  }
  .@{prefixCls}-wrapper-disabled {
    cursor: not-allowed;
  }
  .@{prefixCls}-disabled {
    cursor: not-allowed;
    .@{prefixCls}-inner {
      border-color: #dcdee2;
      background-color: #f3f3f3;
      &:after {
        background-color:#ccc;
      }
    }
  }
  .@{prefixCls} {
    display: inline-block;
    margin-right: 4px;
    white-space: nowrap;
    position: relative;
    line-height: 1;
    vertical-align: middle;
    cursor: pointer;
  }
  .@{prefixCls}-inner {
    display: inline-block;
    width: 14px;
    height: 14px;
    position: relative;
    top: 0;
    left: 0;
    background-color: #fff;
    border: 1px solid #dcdee2;
    border-radius: 50%;
    transition: all .2s ease-in-out;
    &:after {
      position: absolute;
      width: 8px;
      height: 8px;
      left: 2px;
      top: 2px;
      border-radius: 6px;
      display: table;
      border-top: 0;
      border-left: 0;
      content: " ";
      background-color: #2d8cf0;
      opacity: 0;
      transition: all .2s ease-in-out;
    }
  }
  .@{prefixCls}-custom {
    display: none;
  }
  .@{prefixCls}-input {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1;
    opacity: 0;
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// components/Radio/Radio.vue